/**
 * VisibilityRuleBuilder — a `BottomSheet` editing ONE binding's
 * `visibility_rule` (PIM Wave P1 Task 9, design notes Surface 4). Mounted
 * from the indicator control `BindingRow` adds on itself (Task 8 left the
 * mount comment) — this component owns its own `open` state's SHEET
 * CONTENT, but `open`/`onClose` are controlled by the parent row.
 *
 * Backend shape lock verified against `catalog_schema/schemas.py:67-102`
 * (`_check_visibility_rule`, shared by `BindingCreate`/`BindingUpdate`'s
 * `visibility_rule` validator): exactly the three keys
 * `{field_slug, operator, value}` (extra keys 422); `operator` is `"eq"` or
 * `"in"`; `value` is required (any JSON type for `"eq"`) and MUST be a list
 * when `operator === "in"`. `Task 8`'s `VisibilityRule` TS type
 * (`category-bindings/api.ts`) already mirrors this exactly — no correction
 * needed.
 *
 * Candidate controlling fields = select/boolean fields on the category's
 * EFFECTIVE chain, computed ONCE at `BindingEditorPage` (union of direct +
 * ancestor binding field-ids, already-fetched — no re-fetch) and passed down
 * through `BindingRow` as TWO props: `candidateFields` (NON-archived —
 * offered as NEW-rule picks) and `resolutionFields` (same chain, archived
 * fields INCLUDED — used ONLY to resolve the PERSISTED rule's controlling
 * field for display, so an archived-but-still-bound controller is
 * distinguished from a genuinely unresolvable one instead of both falling
 * into the same generic "stale" bucket — post-review fix). This component
 * excludes the edited binding's OWN field from both (a field can't control
 * itself).
 *
 * Cascades (brief §4): changing the controlling field always resets BOTH
 * operator and value (operator re-defaults to `"eq"`; boolean fields default
 * `value` straight to `false` since a boolean rule has no meaningful "empty"
 * state — `Segmented` has no unselected affordance); changing the operator
 * (select fields only — boolean locks `operator` to `"eq"`, control hidden)
 * resets `value` alone.
 *
 * Robustness (brief §6, hardened post-review): the resolved-vs-unresolvable
 * classification of the PERSISTED rule is a RENDER-TIME `useMemo` over the
 * CURRENT `resolutionBySlug` map — never frozen in component state — so it
 * cannot go stale just because the sheet opened before the field drain
 * (`useAllAttributeFields`) or the ancestor-binding fan-out
 * (`useAncestorCategoryBindings`) finished resolving. `candidatesLoading`
 * (threaded down from `BindingEditorPage`, true while either source is still
 * in flight) gates the warning + Clear-as-remedy UI: while loading, the
 * sheet shows a plain neutral loading state instead of ever mis-reporting a
 * healthy rule as unresolvable. Only the admin's EDITABLE DRAFT
 * (field/operator/value selections) lives in state — it is (re-)seeded from
 * the persisted `binding.visibility_rule` every time the sheet opens, AND
 * once more if `candidatesLoading` flips from true to false while the sheet
 * stays open (so a draft seeded blank mid-race catches up once the data
 * finishes loading). The draft never pre-selects an archived field (it only
 * seeds from the non-archived `candidatesBySlug` pick list) — the persisted
 * rule itself is left untouched until the admin explicitly Clears it or
 * picks + Saves a full (non-archived) replacement (`isValid`-gated Save
 * never round-trips a half-picked draft).
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";

import { BottomSheet, Button, Segmented } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { fieldTypeMeta } from "@/features/admin/attribute-fields/lib/fieldTypeMeta";
import type { AttributeField } from "@/features/admin/attribute-fields/api";
import { useAttributeFieldOptions } from "@/features/admin/attribute-fields/hooks";
import type { CategoryBinding, VisibilityRule } from "@/features/admin/category-bindings/api";
import { useUpdateCategoryBinding } from "@/features/admin/category-bindings/hooks";

const SELECT_CLASS =
  "min-h-[44px] w-full rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]";

/** Draft value shape while editing — a select-eq value_slug, a select-in
 * array of value_slugs, a boolean, or `undefined` before anything is picked. */
type DraftValue = string | string[] | boolean | undefined;

export interface VisibilityRuleBuilderProps {
  open: boolean;
  onClose: () => void;
  categoryId: string;
  binding: CategoryBinding;
  /** Already-translated human name of the binding's OWN field, for the
   * sheet title. */
  ownFieldName: string;
  /** NON-archived select/boolean fields on the category's effective chain
   * (superset across every row on the page) — the NEW-rule pick list
   * offered in the field `<select>`. This component filters out the edited
   * binding's own field internally. */
  candidateFields: AttributeField[];
  /** ALL select/boolean fields on the category's effective chain, archived
   * fields INCLUDED — a superset of `candidateFields` used ONLY to resolve
   * the PERSISTED rule's controlling field for display (post-review fix):
   * an archived-but-still-bound controller must still resolve here (marked
   * archived) rather than collapse into the generic "stale" state. */
  resolutionFields: AttributeField[];
  /** True while the field drain or ancestor-binding fan-out this category's
   * candidate/resolution field lists are built from is still in flight
   * (threaded from `BindingEditorPage`, post-review fix). Gates the
   * stale/archived warning + Clear-as-remedy UI so an in-flight resolution
   * never mis-reports a healthy persisted rule as unresolvable. */
  candidatesLoading: boolean;
}

export function VisibilityRuleBuilder({
  open,
  onClose,
  categoryId,
  binding,
  ownFieldName,
  candidateFields,
  resolutionFields,
  candidatesLoading,
}: VisibilityRuleBuilderProps): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();

  // Pick list (NEW-rule authoring) — non-archived only, own field excluded.
  const candidates = useMemo(
    () => candidateFields.filter((f) => f.id !== binding.field_id),
    [candidateFields, binding.field_id],
  );
  const candidatesBySlug = useMemo(
    () => new Map(candidates.map((f) => [f.slug, f])),
    [candidates],
  );

  // Resolution map (DISPLAY of the persisted rule) — archived fields
  // included, own field excluded. Superset of `candidatesBySlug`.
  const resolutionBySlug = useMemo(
    () =>
      new Map(
        resolutionFields.filter((f) => f.id !== binding.field_id).map((f) => [f.slug, f]),
      ),
    [resolutionFields, binding.field_id],
  );

  const hasRule = binding.visibility_rule !== null;
  const persistedFieldSlug = binding.visibility_rule?.field_slug ?? null;
  // A stable string signature of the persisted rule (not the object
  // reference) — the bindings list refetches as a fresh array/objects on
  // EVERY unrelated mutation in this category (reorder, another row's
  // requirement change, etc.); resyncing the draft only when the rule's
  // actual CONTENT changes (not just its reference) avoids clobbering an
  // in-progress edit on a spurious background refetch.
  const ruleSignature = binding.visibility_rule ? JSON.stringify(binding.visibility_rule) : null;

  // RENDER-TIME classification of the PERSISTED rule (post-review fix) —
  // recomputed from the CURRENT `resolutionBySlug` on every render, never
  // frozen. This is what decides stale-vs-archived-vs-healthy; it is
  // deliberately NOT component state, so it can never lag behind a field
  // drain / ancestor-binding fan-out that finishes after the sheet opened.
  const persistedResolvedField = useMemo(
    () => (persistedFieldSlug ? (resolutionBySlug.get(persistedFieldSlug) ?? null) : null),
    [persistedFieldSlug, resolutionBySlug],
  );
  const isArchivedController = persistedResolvedField?.is_archived === true;
  const isUnresolvable = hasRule && persistedResolvedField === null;

  const [fieldSlug, setFieldSlug] = useState<string | null>(null);
  const [operator, setOperator] = useState<"eq" | "in" | null>(null);
  const [value, setValue] = useState<DraftValue>(undefined);

  // (Re-)seed the DRAFT from the persisted rule every time the sheet opens,
  // and again if `candidatesLoading` flips from true to false while the
  // sheet stays open — a sheet opened mid-drain seeds blank the first time
  // (see below), then catches up once the pick list is actually complete.
  // Skipped entirely while still loading: seeding against an incomplete
  // `candidatesBySlug` would (falsely) blank out a resolvable field.
  useEffect(() => {
    if (!open || candidatesLoading) return;
    const rule = binding.visibility_rule;
    if (rule) {
      const resolved = candidatesBySlug.get(rule.field_slug);
      if (resolved) {
        setFieldSlug(rule.field_slug);
        setOperator(rule.operator);
        setValue(rule.value as DraftValue);
      } else {
        // Doesn't resolve among NON-archived candidates — either genuinely
        // stale/unbound, or its controller is archived. Either way the
        // draft starts blank; the admin Clears or picks + Saves a fresh
        // (non-archived) replacement. The render-time memos above (not this
        // effect) drive which warning copy is shown.
        setFieldSlug(null);
        setOperator(null);
        setValue(undefined);
      }
    } else {
      setFieldSlug(null);
      setOperator(null);
      setValue(undefined);
    }
    // `binding` and `candidatesBySlug` deliberately excluded — both churn on
    // every page-wide bindings refetch (see `ruleSignature` note above),
    // reference-changing even when unrelated to THIS row; the effect only
    // needs to re-seed when the sheet (re)opens, the persisted rule's actual
    // content changes, or the candidate data finishes loading — read fresh
    // off `binding`/`candidatesBySlug` at the moment it fires (both are in
    // scope via closure).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ruleSignature, candidatesLoading]);

  const selectedField = fieldSlug ? (candidatesBySlug.get(fieldSlug) ?? null) : null;
  const isBooleanField = selectedField?.field_type === "boolean";
  const isSelectField = selectedField?.field_type === "select";

  const optionsQuery = useAttributeFieldOptions(
    open && isSelectField && selectedField ? selectedField.id : null,
  );
  const allOptions = useMemo(() => optionsQuery.data?.options ?? [], [optionsQuery.data]);

  const selectedSlugsForVisibility = useMemo(() => {
    if (!isSelectField) return new Set<string>();
    if (operator === "in" && Array.isArray(value)) return new Set(value);
    if (operator === "eq" && typeof value === "string" && value !== "") return new Set([value]);
    return new Set<string>();
  }, [isSelectField, operator, value]);

  // Archived options are excluded from NEW selections but a persisted rule
  // referencing an archived option must still render it (marked archived)
  // rather than corrupt the rule (brief §3).
  const visibleOptions = useMemo(
    () =>
      allOptions
        .filter((o) => !o.is_archived || selectedSlugsForVisibility.has(o.value_slug))
        .slice()
        .sort((a, b) => a.sort_order - b.sort_order),
    [allOptions, selectedSlugsForVisibility],
  );

  const handleFieldChange = (nextSlug: string): void => {
    setFieldSlug(nextSlug);
    const nextField = candidatesBySlug.get(nextSlug);
    setOperator("eq");
    // Boolean fields have no meaningful "unset" value (Segmented always
    // highlights a segment) — default straight to `false`; select fields
    // start blank until the admin picks an option.
    setValue(nextField?.field_type === "boolean" ? false : undefined);
  };

  const handleOperatorChange = (next: string): void => {
    if (next !== "eq" && next !== "in") return;
    setOperator(next);
    setValue(undefined);
  };

  const updateMutation = useUpdateCategoryBinding();
  const [pendingAction, setPendingAction] = useState<"save" | "clear" | null>(null);

  const isValid =
    selectedField !== null &&
    operator !== null &&
    (operator === "in"
      ? Array.isArray(value) && value.length > 0
      : value !== undefined && value !== "");

  const handleSave = (): void => {
    if (!selectedField || !operator || !isValid) return;
    const rule: VisibilityRule = { field_slug: selectedField.slug, operator, value };
    setPendingAction("save");
    updateMutation.mutate(
      { categoryId, bindingId: binding.id, body: { visibility_rule: rule } },
      {
        onSuccess: () => {
          toast.success(t("pim.bindings.visibility.saveSuccess"));
          onClose();
        },
        onError: () => toast.error(t("error.generic")),
        onSettled: () => setPendingAction(null),
      },
    );
  };

  const handleClear = (): void => {
    setPendingAction("clear");
    updateMutation.mutate(
      { categoryId, bindingId: binding.id, body: { visibility_rule: null } },
      {
        onSuccess: () => {
          toast.success(t("pim.bindings.visibility.clearSuccess"));
          onClose();
        },
        onError: () => toast.error(t("error.generic")),
        onSettled: () => setPendingAction(null),
      },
    );
  };

  const previewSentence = useMemo(() => {
    if (!selectedField || operator === null) return null;
    if (isBooleanField && typeof value === "boolean") {
      const boolLabel = value
        ? t("pim.bindings.visibility.valueTrue")
        : t("pim.bindings.visibility.valueFalse");
      return t("pim.bindings.visibility.previewEq", { field: selectedField.name_ru, value: boolLabel });
    }
    if (operator === "in" && Array.isArray(value) && value.length > 0) {
      const labels = value.map((slug) => allOptions.find((o) => o.value_slug === slug)?.label_ru ?? slug);
      return t("pim.bindings.visibility.previewIn", {
        field: selectedField.name_ru,
        values: labels.join(", "),
      });
    }
    if (operator === "eq" && typeof value === "string" && value !== "") {
      const label = allOptions.find((o) => o.value_slug === value)?.label_ru ?? value;
      return t("pim.bindings.visibility.previewEq", { field: selectedField.name_ru, value: label });
    }
    return null;
  }, [selectedField, operator, value, isBooleanField, allOptions, t]);

  const hasCandidates = candidates.length > 0;

  // Which warning (if any) to show for the persisted rule — meaningful only
  // once `candidatesLoading` is false (see prop doc); shared by both the
  // Clear-only branch and the full-form branch so the SAME state renders the
  // SAME copy. Archived gets distinct copy (REQ: backend treats an archived
  // controller as absent/fail-hidden, but it's a different situation for the
  // admin than a genuinely unbound/wrong-type field).
  const showResolutionWarning = !candidatesLoading && hasRule && (isUnresolvable || isArchivedController);
  const resolutionWarningText = isArchivedController
    ? t("pim.bindings.visibility.archivedControllerWarning", {
        name: persistedResolvedField?.name_ru ?? persistedFieldSlug ?? "",
      })
    : t("pim.bindings.visibility.staleWarning", { slug: persistedFieldSlug ?? "" });

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={t("pim.bindings.visibility.title", { name: ownFieldName })}
      desktop={isDesktop}
    >
      <div className="flex flex-col gap-4" data-testid="pim-visibility-rule-builder">
        {candidatesLoading ? (
          <>
            <p
              role="status"
              aria-live="polite"
              data-testid="pim-visibility-loading"
              className="text-sm text-[var(--g-secondary)]"
            >
              {t("pim.bindings.visibility.loading")}
            </p>
            <div className="flex justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={onClose} className="min-h-[44px]">
                {t("common.close")}
              </Button>
            </div>
          </>
        ) : !hasCandidates && !hasRule ? (
          <>
            <p className="text-sm text-[var(--g-secondary)]">
              {t("pim.bindings.visibility.noCandidates")}
            </p>
            <div className="flex justify-end">
              <Button type="button" variant="ghost" size="sm" onClick={onClose} className="min-h-[44px]">
                {t("common.close")}
              </Button>
            </div>
          </>
        ) : !hasCandidates && hasRule ? (
          <>
            <div
              role="alert"
              data-testid="pim-visibility-stale-warning"
              className="flex items-start gap-2 rounded-[var(--g-radius-sm)] bg-[var(--g-warn)]/10 p-3 text-sm text-[var(--g-warn)]"
            >
              <AlertTriangle size={16} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0" />
              <span>{resolutionWarningText}</span>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" size="sm" onClick={onClose} className="min-h-[44px]">
                {t("common.close")}
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                loading={pendingAction === "clear"}
                onClick={handleClear}
                data-testid="pim-visibility-clear"
                className="min-h-[44px]"
              >
                {t("pim.bindings.visibility.clear")}
              </Button>
            </div>
          </>
        ) : (
          <>
            {showResolutionWarning ? (
              <div
                role="alert"
                data-testid="pim-visibility-stale-warning"
                className="flex items-start gap-2 rounded-[var(--g-radius-sm)] bg-[var(--g-warn)]/10 p-3 text-sm text-[var(--g-warn)]"
              >
                <AlertTriangle size={16} strokeWidth={1.75} aria-hidden className="mt-0.5 shrink-0" />
                <span>{resolutionWarningText}</span>
              </div>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <label htmlFor="pim-visibility-field" className="text-xs font-semibold text-[var(--g-primary-ink)]">
                {t("pim.bindings.visibility.fieldLabel")}
              </label>
              <select
                id="pim-visibility-field"
                data-testid="pim-visibility-field-select"
                className={SELECT_CLASS}
                value={fieldSlug ?? ""}
                onChange={(event) => handleFieldChange(event.target.value)}
              >
                <option value="" disabled>
                  {t("pim.bindings.visibility.fieldPlaceholder")}
                </option>
                {candidates.map((field) => {
                  const typeMeta = fieldTypeMeta(field.field_type);
                  return (
                    <option key={field.id} value={field.slug}>
                      {typeMeta ? `${field.name_ru} — ${t(typeMeta.labelKey)}` : field.name_ru}
                    </option>
                  );
                })}
              </select>
            </div>

            {selectedField && !isBooleanField ? (
              <div className="flex flex-col gap-1.5">
                <span
                  id="pim-visibility-operator-label"
                  className="text-xs font-semibold text-[var(--g-primary-ink)]"
                >
                  {t("pim.bindings.visibility.operatorLabel")}
                </span>
                <div
                  role="group"
                  aria-labelledby="pim-visibility-operator-label"
                  data-testid="pim-visibility-operator"
                >
                  <Segmented
                    options={[
                      { value: "eq", label: t("pim.bindings.visibility.operatorEq") },
                      { value: "in", label: t("pim.bindings.visibility.operatorIn") },
                    ]}
                    value={operator ?? "eq"}
                    onChange={handleOperatorChange}
                  />
                </div>
              </div>
            ) : null}

            {selectedField && isBooleanField ? (
              <div className="flex flex-col gap-1.5">
                <span
                  id="pim-visibility-value-label"
                  className="text-xs font-semibold text-[var(--g-primary-ink)]"
                >
                  {t("pim.bindings.visibility.valueLabel")}
                </span>
                <div
                  role="group"
                  aria-labelledby="pim-visibility-value-label"
                  data-testid="pim-visibility-value-boolean"
                >
                  <Segmented
                    options={[
                      { value: "true", label: t("pim.bindings.visibility.valueTrue") },
                      { value: "false", label: t("pim.bindings.visibility.valueFalse") },
                    ]}
                    value={typeof value === "boolean" ? String(value) : "false"}
                    onChange={(next) => setValue(next === "true")}
                  />
                </div>
              </div>
            ) : null}

            {selectedField && isSelectField && operator === "eq" ? (
              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="pim-visibility-value-select"
                  className="text-xs font-semibold text-[var(--g-primary-ink)]"
                >
                  {t("pim.bindings.visibility.valueLabel")}
                </label>
                <select
                  id="pim-visibility-value-select"
                  data-testid="pim-visibility-value-select"
                  className={SELECT_CLASS}
                  value={typeof value === "string" ? value : ""}
                  onChange={(event) => setValue(event.target.value)}
                >
                  <option value="" disabled>
                    {t("pim.bindings.visibility.valuePlaceholder")}
                  </option>
                  {visibleOptions.map((option) => (
                    <option key={option.id} value={option.value_slug}>
                      {option.is_archived
                        ? `${option.label_ru} (${t("pim.bindings.visibility.archivedOption")})`
                        : option.label_ru}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            {selectedField && isSelectField && operator === "in" ? (
              <fieldset className="flex flex-col gap-1.5">
                <legend className="text-xs font-semibold text-[var(--g-primary-ink)]">
                  {t("pim.bindings.visibility.valueLabel")}
                </legend>
                <ul className="flex flex-col gap-2" data-testid="pim-visibility-value-checklist">
                  {visibleOptions.map((option) => {
                    const checked = Array.isArray(value) && value.includes(option.value_slug);
                    return (
                      <li key={option.id}>
                        <label className="flex items-center gap-2 rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] p-2 text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(event) => {
                              const current = Array.isArray(value) ? value : [];
                              const next = event.target.checked
                                ? [...current, option.value_slug]
                                : current.filter((slug) => slug !== option.value_slug);
                              setValue(next);
                            }}
                            className="size-5 shrink-0 accent-[var(--g-primary)]"
                          />
                          <span>
                            {option.is_archived
                              ? `${option.label_ru} (${t("pim.bindings.visibility.archivedOption")})`
                              : option.label_ru}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </fieldset>
            ) : null}

            {previewSentence ? (
              <p
                data-testid="pim-visibility-preview"
                className="rounded-[var(--g-radius-sm)] bg-[var(--g-elevated)] p-2.5 text-sm text-[var(--g-primary-ink)]"
              >
                {previewSentence}
              </p>
            ) : null}

            <div className="flex flex-wrap justify-end gap-2">
              {hasRule ? (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  loading={pendingAction === "clear"}
                  disabled={pendingAction === "save"}
                  onClick={handleClear}
                  data-testid="pim-visibility-clear"
                  className="min-h-[44px]"
                >
                  {t("pim.bindings.visibility.clear")}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onClose}
                disabled={pendingAction !== null}
                className="min-h-[44px]"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                variant="primary"
                size="sm"
                loading={pendingAction === "save"}
                disabled={!isValid || pendingAction === "clear"}
                onClick={handleSave}
                data-testid="pim-visibility-save"
                className="min-h-[44px]"
              >
                {t("common.save")}
              </Button>
            </div>
          </>
        )}
      </div>
    </BottomSheet>
  );
}

export default VisibilityRuleBuilder;
